import type { ShortcutRegistrationErrorT } from '../../types';
const errors = {};

// it is useful to track this error, but it can very easily pollute the console.  It would be useful to have a custom throttle here
export const handleShortcutRegistrationError = (error: ShortcutRegistrationErrorT) => {
  const errorString = JSON.stringify(`${error.errorKey}-${error.type}`);
  const now = new Date();
  if (errors[errorString] && now.getTime() - errors[errorString] <= 5000) {
    return;
  }
  errors[errorString] = now.getTime();
  console.error(error);
};
export const PLATFORM_MOD_LABEL = '⌘/Ctrl';
export const isMacOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
};
export const getPrimaryModifierKey = (): 'Meta' | 'Ctrl' => isMacOS() ? 'Meta' : 'Ctrl';

/** Replaces `Mod` with Meta (Mac) or Ctrl (Windows/Linux) before registration. */
export const resolvePlatformKeys = (keys: string[]): string[] => keys.map(key => key === 'Mod' ? getPrimaryModifierKey() : key);
export const MODIFIER_KEYS_MAP = {
  Alt: 'altKey',
  Command: 'commandKey',
  Control: 'ctrlKey',
  Meta: 'metaKey',
  Shift: 'shiftKey'
};
export const getSerializedKeys = (keys: string[]) => {
  return keys.map(normalizeKeyToken).sort().join();
};
const isElementInput = node => node.tagName === 'INPUT';
const isElementTextArea = node => node.tagName === 'TEXTAREA';
const isContentEditableNode = node => {
  const isContentEditable = node.attributes?.contentEditable?.value === 'true';
  return isContentEditable;
};
export const isEventTriggeredFromEditableContainer = targetNode => {
  return isElementInput(targetNode) || isElementTextArea(targetNode) || isContentEditableNode(targetNode);
};
export const SUPPORTED_KEYBOARD_EVENTS = ({
  KEYDOWN: 'keydown',
  KEYUP: 'keyup',
  VISIBILITY_CHANGE: 'visibilitychange'
} as const);
export const CUSTOM_KEYBOARD_EVENTS = ({
  LONG_PRESS: 'longpress',
  TOGGLE: 'toggle'
} as const);

// Returns a canonical token for a registered key name
// - Normalizes letters to 'KeyX' and digits to 'DigitN'
// - Normalizes 'Ctrl' to 'Control'
// - Leaves other special keys (e.g. ArrowUp, Enter) as-is
export const normalizeKeyToken = (key: string): string => {
  if (!key) return key;
  const normalized = key === 'Ctrl' ? 'Control' : key;
  if (normalized.startsWith('Key') || normalized.startsWith('Digit')) return normalized;
  const upper = normalized.toUpperCase();
  if (upper.length === 1 && upper >= 'A' && upper <= 'Z') return `Key${upper}`;
  if (/^[0-9]$/.test(normalized)) return `Digit${normalized}`;
  return normalized;
};

// Normalizes a KeyboardEvent's primary key into a canonical token that is not affected
// by modifier keys like Alt changing the printable character. Prefers hardware code.
export const normalizeEventKey = (event: KeyboardEvent): string => {
  const code = (event as KeyboardEvent).code;
  if (code && (code.startsWith('Key') || code.startsWith('Digit'))) return code;
  return (event as KeyboardEvent).key;
};